import React, { useState, useEffect, useMemo } from 'react';
import { 
  Home, 
  BarChart3, 
  ShoppingCart, 
  List, 
  Plus, 
  Minus, 
  Settings,
  Menu,
  X,
  Calendar,
  TrendingUp,
  Package,
  Users,
  RefreshCw,
  Filter,
  Search,
  Bell,
  User
} from 'lucide-react';

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedChart, setSelectedChart] = useState('monthly');
  const [dashboardData, setDashboardData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('Peso');
  const [exchangeRate, setExchangeRate] = useState(55.0);
  const [badges, setBadges] = useState({});
  const [filterType, setFilterType] = useState('Department');
  const [filterValue, setFilterValue] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedClassification, setSelectedClassification] = useState('');

  const sidebarItems = [
    { icon: Home, label: 'Main', active: false },
    { icon: BarChart3, label: 'Dashboard', active: true },
    { icon: ShoppingCart, label: 'Purchase Request Input', active: false },
    { icon: List, label: 'Purchase Request List', active: false },
    { icon: Plus, label: 'Inventory - Add Items', active: false },
    { icon: Minus, label: 'Inventory - Withdraw Items', active: false },
    { icon: Settings, label: 'Maintenance', active: false },
  ];

  const chartOptions = [
    { id: 'monthly', label: 'Monthly PR Chart', color: 'bg-blue-500' },
    { id: 'department', label: 'Department PR Chart', color: 'bg-green-500' },
    { id: 'classification', label: 'PR Classification Chart', color: 'bg-yellow-500' },
  ];

  // Fetch currency exchange rate
  useEffect(() => {
    const fetchExchangeRate = async () => {
      // Mock function since apiService is not available
      // const result = await apiService.getCurrencyRates();
      // if (result.success) {
      //   setExchangeRate(result.data);
      // }
    };
    fetchExchangeRate();
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
    fetchBadges();
  }, [currency]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Mock function since apiService is not available
      // const result = await apiService.getDashboardData('chartt', currency);
      // if (result.success) {
      //   const processedData = processApiData(result.data);
      //   setDashboardData(processedData);
      //   setChartData(processedData);
      // }
      
      // Mock data for demonstration
      const mockData = [
        {
          id: 1,
          pr_month: 'January',
          year: '2025',
          department: 'IT Department',
          classification: 'Office Supplies',
          item: 'Laptops',
          price_price: '50000',
          currency: 'Peso',
          mrf: JSON.stringify([{item: 'Laptops', quantity: 10}]),
          price: JSON.stringify([{price: 50000, currency: 'Peso'}])
        },
        {
          id: 2,
          pr_month: 'February',
          year: '2025',
          department: 'HR Department',
          classification: 'Equipment',
          item: 'Printers',
          price_price: '25000',
          currency: 'Peso',
          mrf: JSON.stringify([{item: 'Printers', quantity: 5}]),
          price: JSON.stringify([{price: 25000, currency: 'Peso'}])
        },
        {
          id: 3,
          pr_month: 'March',
          year: '2025',
          department: 'Finance Department',
          classification: 'Software',
          item: 'Accounting Software',
          price_price: '1200',
          currency: 'Dollar',
          mrf: JSON.stringify([{item: 'Software License', quantity: 1}]),
          price: JSON.stringify([{price: 1200, currency: 'Dollar'}])
        }
      ];
      
      const processedData = processApiData(mockData);
      setDashboardData(processedData);
      setChartData(processedData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      // Mock function since apiService is not available
      // const result = await apiService.getBadgeData();
      // if (result.success) {
      //   setBadges(result.data);
      // }
      setBadges({ forapproval: 5 });
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  // Process API data similar to C# JSON processing
  const processApiData = (jsonData) => {
    if (!jsonData || !Array.isArray(jsonData)) return [];

    const processedData = [];

    jsonData.forEach(item => {
      if (item.mrf && typeof item.mrf === 'string' && item.mrf.trim() !== '') {
        try {
          const mrfArray = JSON.parse(item.mrf);
          mrfArray.forEach(mrfItem => {
            const mergedObject = { ...item };
            delete mergedObject.mrf;
            delete mergedObject.price;
            
            // Merge MRF data
            Object.assign(mergedObject, mrfItem);
            
            // Process price data
            if (item.price && typeof item.price === 'string' && item.price.trim() !== '') {
              try {
                const priceArray = JSON.parse(item.price);
                if (priceArray.length > 0) {
                  const priceItem = priceArray[0];
                  Object.keys(priceItem).forEach(key => {
                    mergedObject[`price_${key}`] = priceItem[key];
                  });
                }
              } catch {
                mergedObject.price_value = item.price;
              }
            }
            
            processedData.push(mergedObject);
          });
        } catch {
          // If MRF parsing fails, add item as-is
          const mergedObject = { ...item };
          processPrice(mergedObject, item.price);
          processedData.push(mergedObject);
        }
      } else {
        // Add items without MRF
        const mergedObject = { ...item };
        processPrice(mergedObject, item.price);
        processedData.push(mergedObject);
      }
    });

    return processedData;
  };

  const processPrice = (mergedObject, priceData) => {
    if (priceData && typeof priceData === 'string' && priceData.trim() !== '') {
      try {
        const priceArray = JSON.parse(priceData);
        if (priceArray.length > 0) {
          const priceItem = priceArray[0];
          Object.keys(priceItem).forEach(key => {
            mergedObject[`price_${key}`] = priceItem[key];
          });
        }
      } catch {
        mergedObject.price_value = priceData;
      }
    }
  };

  // Calculate dashboard statistics
  const dashboardStats = useMemo(() => {
    if (!dashboardData.length) {
      return {
        totalAmount: 0,
        totalNoPR: 0,
        totalPRThisMonth: 0,
        mostRequestedItem: 'N/A',
        topDepartmentRequestor: 'N/A'
      };
    }

    // Total Amount (convert to PHP)
    const totalAmount = dashboardData
      .filter(item => item.price_price && item.currency)
      .reduce((sum, item) => {
        let amount = parseFloat(item.price_price) || 0;
        const itemCurrency = item.currency?.toUpperCase();
        
        if (['DOLLAR', 'USD', '$'].includes(itemCurrency)) {
          amount *= exchangeRate;
        }
        
        return sum + amount;
      }, 0);

    // Total No. of PR
    const totalNoPR = dashboardData.length;

    // Total PR this Month
    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
    const currentYear = currentDate.getFullYear().toString();
    
    const totalPRThisMonth = dashboardData.filter(item => 
      item.pr_month === currentMonth && item.year === currentYear
    ).length;

    // Most Requested Item
    const itemCounts = {};
    dashboardData.forEach(item => {
      if (item.item) {
        itemCounts[item.item] = (itemCounts[item.item] || 0) + 1;
      }
    });
    const mostRequestedItem = Object.keys(itemCounts).length > 0 
      ? Object.keys(itemCounts).reduce((a, b) => itemCounts[a] > itemCounts[b] ? a : b)
      : 'N/A';

    // Top Department Requestor
    const deptCounts = {};
    dashboardData.forEach(item => {
      if (item.department) {
        deptCounts[item.department] = (deptCounts[item.department] || 0) + 1;
      }
    });
    const topDepartmentRequestor = Object.keys(deptCounts).length > 0
      ? Object.keys(deptCounts).reduce((a, b) => deptCounts[a] > deptCounts[b] ? a : b)
      : 'N/A';

    return {
      totalAmount,
      totalNoPR,
      totalPRThisMonth,
      mostRequestedItem,
      topDepartmentRequestor
    };
  }, [dashboardData, exchangeRate]);

  // Generate chart data based on selected chart type
  const generateChartData = () => {
    if (!chartData.length) return { labels: [], datasets: [] };

    let groupedData = {};
    
    switch (selectedChart) {
      case 'monthly':
        groupedData = chartData
          .filter(item => item.pr_month && item.price_price && item.currency)
          .reduce((acc, item) => {
            const month = item.pr_month;
            let amount = parseFloat(item.price_price) || 0;
            const itemCurrency = item.currency?.toUpperCase();
            
            if (['DOLLAR', 'USD', '$'].includes(itemCurrency)) {
              amount *= exchangeRate;
            }
            
            acc[month] = (acc[month] || 0) + amount;
            return acc;
          }, {});
        break;
        
      case 'department':
        if (selectedDepartment) {
          groupedData = chartData
            .filter(item => 
              item.department === selectedDepartment && 
              item.pr_month && 
              item.price_price && 
              item.currency
            )
            .reduce((acc, item) => {
              const month = item.pr_month;
              let amount = parseFloat(item.price_price) || 0;
              const itemCurrency = item.currency?.toUpperCase();
              
              if (['DOLLAR', 'USD', '$'].includes(itemCurrency)) {
                amount *= exchangeRate;
              }
              
              acc[month] = (acc[month] || 0) + amount;
              return acc;
            }, {});
        }
        break;
        
      case 'classification':
        if (selectedClassification) {
          groupedData = chartData
            .filter(item => 
              item.classification === selectedClassification && 
              item.pr_month && 
              item.price_price && 
              item.currency
            )
            .reduce((acc, item) => {
              const month = item.pr_month;
              let amount = parseFloat(item.price_price) || 0;
              const itemCurrency = item.currency?.toUpperCase();
              
              if (['DOLLAR', 'USD', '$'].includes(itemCurrency)) {
                amount *= exchangeRate;
              }
              
              acc[month] = (acc[month] || 0) + amount;
              return acc;
            }, {});
        }
        break;
    }

    const labels = Object.keys(groupedData).sort();
    const data = labels.map(label => groupedData[label]);

    return {
      labels,
      datasets: [
        {
          label: `${selectedChart === 'monthly' ? 'Monthly' : selectedChart === 'department' ? selectedDepartment : selectedClassification} Total (PHP)`,
          data,
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1,
          borderRadius: 8,
        },
      ],
    };
  };

  // Get unique values for filters
  const getUniqueValues = (field) => {
    const currentYear = new Date().getFullYear().toString();
    return [...new Set(
      chartData
        .filter(item => item.year === currentYear && item[field])
        .map(item => item[field])
    )].sort();
  };

  // Simple Bar Chart Component (since Chart.js isn't available)
  const SimpleBarChart = ({ data }) => {
    if (!data.labels || !data.datasets || data.labels.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">No data available</p>
        </div>
      );
    }

    const maxValue = Math.max(...data.datasets[0].data);
    
    return (
      <div className="h-full flex items-end justify-around px-4 pb-4">
        {data.labels.map((label, index) => {
          const value = data.datasets[0].data[index];
          const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
          
          return (
            <div key={index} className="flex flex-col items-center space-y-2">
              <div className="text-xs text-gray-600">₱{value.toLocaleString()}</div>
              <div
                className="w-8 bg-gradient-to-t from-blue-500 to-blue-600 rounded-t-sm"
                style={{ height: `${height}%`, minHeight: '4px' }}
              />
              <div className="text-xs text-gray-500 rotate-45 origin-bottom-left">
                {label}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const statsCards = [
    { 
      title: 'Total Amount', 
      value: `₱${dashboardStats.totalAmount.toLocaleString()}`, 
      icon: TrendingUp, 
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    { 
      title: 'Total No. of PR', 
      value: dashboardStats.totalNoPR.toLocaleString(), 
      icon: Package, 
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    { 
      title: 'Total PR this Month', 
      value: dashboardStats.totalPRThisMonth.toLocaleString(), 
      icon: Calendar, 
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    { 
      title: 'Most Requested Item', 
      value: dashboardStats.mostRequestedItem, 
      icon: ShoppingCart, 
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    },
    { 
      title: 'Top Department Requestor', 
      value: dashboardStats.topDepartmentRequestor, 
      icon: Users, 
      color: 'from-yellow-500 to-yellow-600',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600'
    },
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-4 bg-gradient-to-r from-purple-600 to-purple-700">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-purple-600 font-bold text-lg">P</span>
            </div>
            <span className="text-white text-lg font-semibold">Purchase Request System</span>
          </div>
          <button 
            onClick={() => setIsSidebarOpen(false)}
            className="text-white lg:hidden"
          >
            <X size={24} />
          </button>
        </div>
        
        <nav className="mt-6 px-3">
          {sidebarItems.map((item, index) => (
            <a
              key={index}
              href="#"
              className={`flex items-center justify-between px-4 py-3 mb-2 text-sm font-medium rounded-lg transition-colors ${
                item.active 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center">
                <item.icon className="mr-3" size={20} />
                {item.label}
              </div>
              {/* Badge display */}
              {item.label === 'Purchase Request List' && badges.forapproval > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  {badges.forapproval}
                </span>
              )}
            </a>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="text-gray-500 lg:hidden"
              >
                <Menu size={24} />
              </button>
              
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-700">A</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900">Admin</span>
                  <p className="text-xs text-gray-500">Last login was 2 hours ago</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Today • {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              
              {/* Currency Selector
              <select 
                value={currency} 
                onChange={(e) => setCurrency(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Peso">Peso</option>
                <option value="Dollar">Dollar</option>
              </select> */}
              
              {/* Refresh Button */}
              <button
                onClick={fetchDashboardData}
                disabled={loading}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 rounded-md hover:bg-gray-100"
              >
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          {/* Dashboard Header */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">Dashboard</h1>
                <p className="text-teal-100 mt-1">Home  Main Dashboard</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="pl-10 pr-4 py-2 w-64 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <button className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg">
                  <Search size={20} />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Chart Selection */}
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Chart Selection</h2>
              <div className="flex flex-wrap gap-3">
                {chartOptions.map((chart) => (
                  <button
                    key={chart.id}
                    onClick={() => setSelectedChart(chart.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                      selectedChart === chart.id
                        ? `${chart.color} text-white shadow-lg`
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm'
                    }`}
                  >
                    {chart.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Filter Controls */}
            {(selectedChart === 'department' || selectedChart === 'classification') && (
              <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700">
                    {selectedChart === 'department' ? 'Select Department:' : 'Select PR Classification:'}
                  </label>
                  <select
                    value={selectedChart === 'department' ? selectedDepartment : selectedClassification}
                    onChange={(e) => {
                      if (selectedChart === 'department') {
                        setSelectedDepartment(e.target.value);
                      } else {
                        setSelectedClassification(e.target.value);
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select...</option>
                    {getUniqueValues(selectedChart === 'department' ? 'department' : 'classification').map(value => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Main Chart Area */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">
                  {chartOptions.find(c => c.id === selectedChart)?.label}
                </h2>
              </div>
              
              <div className="relative h-80">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <RefreshCw className="mx-auto mb-2 text-gray-400 animate-spin" size={48} />
                      <p className="text-gray-500">Loading chart data...</p>
                    </div>
                  </div>
                ) : (
                  <SimpleBarChart data={generateChartData()} />
                )}
              </div>
            </div>

            {/* Statistics Cards */}
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Statistics Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                {statsCards.map((stat, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-600 mb-1 truncate">{stat.title}</p>
                        <p className="text-lg font-bold text-gray-900 truncate">{stat.value}</p>
                      </div>
                      <div className={`${stat.bgColor} p-3 rounded-full ml-2 flex-shrink-0`}>
                        <stat.icon className={`${stat.textColor} h-5 w-5`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;